"use client";

import type * as React from "react";
import { cn } from "@/lib/utils";
import { McpToolkitAvatar, type McpToolkitAvatarProps } from "./toolkit-avatar";

export type McpToolkitGroupHeaderProps = {
  /** Toolkit display name — rendered with `capitalize` styling. */
  name: string;
  /** Branded-icon lookup key passed through to `McpToolkitAvatar`. */
  toolkitId?: string;
  /** Avatar size. Defaults to `"sm"` to match in-line section headers. */
  avatarSize?: McpToolkitAvatarProps["size"];
  /**
   * Heading element used for the name. Choose based on document outline —
   * default `h4` fits inside a section nested under the picker's `h3`.
   */
  headingAs?: "h2" | "h3" | "h4" | "h5";
  /**
   * Right-of-name count line — typically "N selected" or just a raw tool
   * count. Wrap free-form text in a `<span>` for consistent styling.
   */
  count?: React.ReactNode;
  /**
   * Optional trailing slot rendered after the count (e.g. an inline "Add N
   * more" expander button).
   */
  trailing?: React.ReactNode;
} & Omit<React.ComponentProps<"div">, "children">;

/**
 * Section header for a toolkit group: branded avatar, name, count, and an
 * optional trailing slot for inline actions. Used at the top of every
 * "tools grouped under their server" list — search results, the drill-in
 * view's identity strip, and the grouped selection review.
 */
export function McpToolkitGroupHeader({
  name,
  toolkitId,
  avatarSize = "sm",
  headingAs: Heading = "h4",
  count,
  trailing,
  className,
  ...rest
}: McpToolkitGroupHeaderProps) {
  return (
    <div
      {...rest}
      className={cn(
        "flex flex-wrap items-center justify-between gap-x-3 gap-y-1",
        className
      )}
      data-slot="mcp-toolkit-group-header"
    >
      <div className="flex min-w-0 items-center gap-2">
        <McpToolkitAvatar name={name} size={avatarSize} toolkitId={toolkitId} />
        <Heading className="truncate font-semibold text-base capitalize">
          {name}
        </Heading>
        {count !== undefined ? (
          <span className="shrink-0 text-muted-foreground text-xs tabular-nums">
            {count}
          </span>
        ) : null}
      </div>
      {trailing ? <div className="flex items-center">{trailing}</div> : null}
    </div>
  );
}
